import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bookingsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, FileCheck, Loader2, CheckCircle } from 'lucide-react';

export default function ContractPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState({});

  useEffect(() => {
    bookingsAPI.getContract(bookingId)
      .then(r => {
        setData(r.data);
        if (r.data.contract?.status === 'signed') {
          const all = {};
          r.data.obligations.forEach((_, i) => all[i] = true);
          setChecked(all);
        }
      })
      .catch(() => toast.error('Erreur lors du chargement'))
      .finally(() => setLoading(false));
  }, [bookingId]);

  const allChecked = data?.obligations?.every((_, i) => checked[i]);
  const alreadySigned = data?.contract?.status === 'signed';

  const handleAccept = async () => {
    if (!allChecked) { toast.error('Veuillez accepter toutes les obligations'); return; }
    setSaving(true);
    try {
      await bookingsAPI.acceptContract(bookingId, { accepted_obligations: data.obligations });
      toast.success('Contrat accepté et signé !');
      setData(prev => ({ ...prev, contract: { ...prev.contract, status: 'signed' } }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
      <Loader2 className="w-8 h-8 animate-spin text-[#00FF00]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-8" data-testid="contract-page">
      <div className="max-w-2xl mx-auto px-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/50 hover:text-[#00FF00] text-sm mb-6 transition-colors" data-testid="contract-back-btn">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        <div className="flex items-center gap-3 mb-8">
          <FileCheck className="w-8 h-8 text-[#00FF00]" />
          <h1 className="font-heading text-3xl font-bold text-white">Contrat de participation</h1>
        </div>

        {data?.booking && (
          <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-5 mb-6">
            <p className="text-white font-medium">{data.booking.activity_name}</p>
            <p className="text-slate-500 text-sm mt-1">{data.booking.date} à {data.booking.time_slot} — {data.booking.duration_minutes} min</p>
          </div>
        )}

        {alreadySigned && (
          <div className="bg-[#00FF00]/10 border border-[#00FF00]/30 rounded-2xl p-5 mb-6 flex items-center gap-3" data-testid="contract-signed-badge">
            <CheckCircle className="w-6 h-6 text-[#00FF00]" />
            <div>
              <p className="text-[#00FF00] font-bold">Contrat signé</p>
              <p className="text-slate-500 text-xs">Signé le {data.contract.signed_at?.slice(0, 10)}</p>
            </div>
          </div>
        )}

        <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-6">
          <h2 className="font-heading text-lg font-bold text-white mb-4">Obligations du participant</h2>
          <p className="text-slate-500 text-xs mb-6">Veuillez lire et accepter chaque obligation pour valider votre participation.</p>

          <div className="space-y-4">
            {data?.obligations?.map((obligation, i) => (
              <label key={i} className={`flex items-start gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
                checked[i] ? 'bg-[#00FF00]/5 border-[#00FF00]/30' : 'bg-[#0A0A0A] border-[#2A2A2A] hover:border-[#333]'
              } ${alreadySigned ? 'pointer-events-none' : ''}`} data-testid={`contract-obligation-${i}`}>
                <Checkbox checked={!!checked[i]} onCheckedChange={v => setChecked(prev => ({ ...prev, [i]: v }))} disabled={alreadySigned}
                  className="mt-0.5 border-[#333] data-[state=checked]:bg-[#00FF00] data-[state=checked]:border-[#00FF00]" />
                <span className="text-sm text-slate-300 leading-relaxed">{obligation}</span>
              </label>
            ))}
          </div>

          {!alreadySigned && (
            <Button onClick={handleAccept} disabled={saving || !allChecked}
              className="mt-6 w-full bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold" data-testid="contract-accept-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileCheck className="w-4 h-4 mr-2" />}
              Accepter et signer le contrat
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
